import { useEffect, useState } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { fetchBillingProfile } from '../api/billingApi';
import { hasValidPaymentMethod } from '../utils/paymentMethods';
import { subscribeToBillingProfile } from './useBillingProfile';

/**
 * Whether the top navigation should offer "Upgrade to Pro".
 *
 * The prompt is shown only when all of the following hold:
 *   - the platform is SaaS with billing enabled (``is_saas`` and
 *     ``billing_enabled`` from ``/auth/debug``);
 *   - the user is an Admin — the only role that can open Plans & Billing
 *     and call ``GET billing/profile`` (Editors / Viewers get 403);
 *   - the billing profile loaded and has no valid (non-expired) card.
 *
 * A failed or still-pending profile request hides the prompt rather than
 * risk a false call to action. Profiles loaded or mutated through
 * ``useBillingProfile`` (the Plans & Billing tab) are picked up live, so
 * adding a card dismisses the prompt without another request.
 */
export function useUpgradeToProPrompt() {
    const { isSaas, billingEnabled, isAuthEnabled, role } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';
    const enabled = isSaas === true && billingEnabled === true && isAdmin;
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        if (!enabled) {
            setProfile(null);
            return undefined;
        }
        let active = true;
        const unsubscribe = subscribeToBillingProfile((next) => {
            if (active) setProfile(next);
        });
        fetchBillingProfile()
            .then((next) => {
                if (active) setProfile(next);
            })
            .catch(() => {
                if (active) setProfile(null);
            });
        return () => {
            active = false;
            unsubscribe();
        };
    }, [enabled]);

    const showUpgradeToPro = enabled && profile !== null && !hasValidPaymentMethod(profile.payment_methods);

    return { showUpgradeToPro };
}

export default useUpgradeToProPrompt;
